import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import { iEntry } from "../state/Entry";
import { iReference } from "../state/Reference";
import * as Rule from "../state/Rule";
import * as Status from "../Status";
import { toStream, toVariable } from "../toStream";
import { Scalar, ScalarProvider, WeakCache, createVariable, createExpressionBuilder } from "../../../../../stream";
import { validateText, getFileExtensionValidationPattern, getTextValidationPattern } from "../state/textValidation";
import { getUpperLimit, getLowerLimit, mustBePositive } from "../state/numberLimit";
import { iCollection } from "../state/Collection";

export const Indirect = Symbol.for("indirect");
export type Indirect = typeof Indirect;

const ruleValid = WeakCache<api_cs.Crules, Scalar<boolean>>((rule) => {
	if (!Rule.isMandatory(rule)) return true;
	return createVariable(
		createExpressionBuilder(Rule.synchronized(rule)).pipe((synchronized) => {
			switch (synchronized) {
				case Status.Pending:
				case Status.Unknown:
				case "yes":
					rule.properties.is_valid.ensure("yes", {});
					return true;
				case Status.Failed:
					return createExpressionBuilder(Rule.node(rule)).pipe((node) => {
						if (Status.isResolved(node)) {
							rule.properties.is_valid.ensure("yes", {});
							return true;
						} else {
							rule.properties.is_valid.ensure("no", {});
							return false;
						}
					})();
			}
		})(),
		false
	);
});
const rulesValid = WeakCache<api_cs.Cwhere_rules, ScalarProvider<boolean>>((rules) =>
	createVariable(
		toStream(rules.properties.rules)
			.map(([, rule]) => ruleValid(rule))
			.reduce((rules_valid, rule_valid) => rules_valid && rule_valid, true)(),
		false
	)
);

const textDoesNotMatchPattern = WeakCache<api_cs.Ctext__type__property, Scalar<boolean>>((property) => {
	const validation_pattern = getTextValidationPattern(property);
	if (!validation_pattern) return false;
	return createVariable(
		toStream(property.properties.is_editable).stateSwitch({
			yes: ($) =>
				toStream(property.component_root.properties.location).stateSwitch({
					remote: () =>
						toStream($.properties.modified).stateSwitch({
							no: () => false,
							yes: ($) => toStream($, "value").pipe((value) => !validateText(value, validation_pattern))(),
						})(),
					local: () =>
						toStream(property.properties.is_set)
							.stateSwitch({
								no: () => "",
								yes: ($) => toVariable($, "text"),
							})
							.pipe((value) => !validateText(value, validation_pattern))(),
				})(),
			no: () => false,
		})(),
		false
	);
});
const textKeyIsNotUnique = WeakCache<api_cs.Ctext__type__property, Scalar<boolean>>((property) => {
	const is_key = property.properties.is_key.state;
	switch (is_key.name) {
		case "no":
			return false;
		case "yes":
			return createVariable(
				createExpressionBuilder(iEntry(is_key.node.inferences.entry()).keyUnique).pipe((unique) =>
					unique === "no" ? true : false
				)(),
				false
			);
	}
});
const textReferenceIsResolved = WeakCache<api_cs.Ctext__type__property, Scalar<boolean>>((property) => {
	const navigable = property.properties.navigable.state;
	switch (navigable.name) {
		case "no":
			return true;
		case "yes":
			if (!iReference(navigable.node).isMandatory) return true;
			return createVariable(
				createExpressionBuilder(iReference(navigable.node).synchronized).pipe((synchronized) => {
					switch (synchronized) {
						case Status.Pending:
						case Status.Unknown:
						case "yes":
							return true;
						case Status.Failed:
							return createExpressionBuilder(iReference(navigable.node).expression).pipe((node) =>
								Status.isResolved(node)
							)();
					}
				})(),
				false
			);
	}
});
const textReferencedNodeValid = WeakCache<api_cs.Ctext__type__property, Scalar<boolean>>((property) => {
	const navigable = property.properties.navigable.state;
	switch (navigable.name) {
		case "no":
			return true;
		case "yes":
			if (!iReference(navigable.node).isMandatory) return true;
			return createVariable(
				createExpressionBuilder(iReference(navigable.node).synchronized).pipe((synchronized) => {
					switch (synchronized) {
						case Status.Pending:
						case Status.Unknown:
						case "yes":
							return true;
						case Status.Failed:
							return createExpressionBuilder(iReference(navigable.node).referencedEntry).pipe((entry) => {
								if (!Status.isResolved(entry)) return true;
								return toStream(entry.properties.location).stateSwitch({
									local: () =>
										createExpressionBuilder(nodeValid(entry.properties.node)).pipe((valid) =>
											valid === false ? false : true
										)(),
									remote: () => true,
								})();
							})();
					}
				})(),
				false
			);
	}
});
const textRulesValid = WeakCache<api_cs.Ctext__type__property, Scalar<boolean>>((property) => {
	const navigable = property.properties.navigable.state;
	switch (navigable.name) {
		case "no":
			return true;
		case "yes":
			return createVariable(createExpressionBuilder(rulesValid(navigable.node.properties.rules))(), false);
	}
});
const textValid = WeakCache<api_cs.Ctext__type__property, ScalarProvider<boolean | Indirect>>((property) =>
	createVariable(
		createExpressionBuilder(textDoesNotMatchPattern(property))
			.and(textKeyIsNotUnique(property))
			.and(textReferenceIsResolved(property))
			.and(textReferencedNodeValid(property))
			.and(textRulesValid(property))
			.pipe(
				([
					[[[text_does_not_match_pattern, key_is_not_unique], reference_is_resolved], referenced_node_valid],
					rules_valid,
				]) => {
					const direct_invalid =
						text_does_not_match_pattern || key_is_not_unique || !reference_is_resolved || !rules_valid;
					if (direct_invalid || !referenced_node_valid) {
						const invalid = property.properties.is_valid.ensure("no", {
							"text does not match pattern": ["no", {}],
							"key is not unique": ["no", {}],
							"reference not resolved": ["no", {}],
							"referenced node not valid": ["no", {}],
							"invalid rules": ["no", {}],
						});
						if (text_does_not_match_pattern) {
							invalid.properties.text_does_not_match_pattern.ensure("yes", {
								pattern: getTextValidationPattern(property)!.properties.regular_expression,
							});
						} else {
							invalid.properties.text_does_not_match_pattern.ensure("no", {});
						}
						invalid.properties.key_is_not_unique.ensure(key_is_not_unique ? "yes" : "no", {});
						invalid.properties.reference_not_resolved.ensure(reference_is_resolved ? "no" : "yes", {});
						invalid.properties.referenced_node_not_valid.ensure(referenced_node_valid ? "no" : "yes", {});
						invalid.properties.invalid_rules.ensure(rules_valid ? "no" : "yes", {});
						if (!direct_invalid) {
							return Indirect;
						}
						return false;
					} else {
						property.properties.is_valid.ensure("yes", {});
						return true;
					}
				}
			)(),
		false
	)
);

const numberNotSet = WeakCache<api_cs.Cnumber__type__property, ScalarProvider<boolean>>((property) =>
	createVariable(
		toStream(property.properties.is_editable).stateSwitch({
			yes: ($) =>
				toStream(property.component_root.properties.location).stateSwitch({
					remote: () => false,
					local: () =>
						toStream($.properties.modified).stateSwitch({
							no: () => true,
							yes: () => false,
						})(),
				})(),
			no: () => false,
		})(),
		false
	)
);
const numberExceedsMaximum = WeakCache<api_cs.Cnumber__type__property, Scalar<false | number>>((property) => {
	const limit = getUpperLimit(property);
	if (limit === undefined) return false;
	return createVariable(
		toStream(property.properties.is_editable).stateSwitch({
			yes: ($) =>
				toStream($.properties.modified).stateSwitch({
					no: () => false as false | number,
					yes: ($) =>
						createExpressionBuilder(limit)
							.and(toVariable($, "number"))
							.pipe(([maximum, value]) => (value > maximum ? maximum : false))(),
				})(),
			no: () => false as false | number,
		})(),
		false
	);
});
const numberExceedsMinimum = WeakCache<api_cs.Cnumber__type__property, Scalar<false | number>>((property) => {
	const limit = getLowerLimit(property);
	if (limit === undefined) return false;
	return createVariable(
		toStream(property.properties.is_editable).stateSwitch({
			yes: ($) =>
				toStream($.properties.modified).stateSwitch({
					no: () => false as false | number,
					yes: ($) =>
						createExpressionBuilder(limit)
							.and(toVariable($, "number"))
							.pipe(([minimum, value]) => (value < minimum ? minimum : false))(),
				})(),
			no: () => false as false | number,
		})(),
		false
	);
});
const numberShouldBePositive = WeakCache<api_cs.Cnumber__type__property, Scalar<boolean>>((property) => {
	if (!mustBePositive(property)) return false;
	return createVariable(
		toStream(property.properties.is_editable).stateSwitch({
			yes: ($) =>
				toStream($.properties.modified).stateSwitch({
					no: () => false,
					yes: ($) => toStream($, "number").pipe((value) => value <= 0)(),
				})(),
			no: () => false,
		})(),
		false
	);
});
const numberValid = WeakCache<api_cs.Cnumber__type__property, ScalarProvider<boolean>>((property) =>
	createVariable(
		createExpressionBuilder(numberNotSet(property))
			.and(numberExceedsMaximum(property))
			.and(numberExceedsMinimum(property))
			.and(numberShouldBePositive(property))
			.pipe(([[[not_set, exceeds_maximum], exceeds_minimum], should_be_positive]) => {
				if (not_set || exceeds_maximum !== false || exceeds_minimum !== false || should_be_positive) {
					const invalid = property.properties.is_valid.ensure("no", {
						"not set": ["no", {}],
						"exceeds maximum": ["no", {}],
						"exceeds minimum": ["no", {}],
						"should be positive": ["no", {}],
						"not a number": ["no", {}],
					});
					if (exceeds_maximum !== false) {
						invalid.properties.exceeds_maximum.ensure("yes", {
							maximum: exceeds_maximum,
						});
					} else {
						invalid.properties.exceeds_maximum.ensure("no", {});
					}
					if (exceeds_minimum !== false) {
						invalid.properties.exceeds_minimum.ensure("yes", {
							minimum: exceeds_minimum,
						});
					} else {
						invalid.properties.exceeds_minimum.ensure("no", {});
					}
					invalid.properties.not_set.ensure(not_set ? "yes" : "no", {});
					invalid.properties.should_be_positive.ensure(should_be_positive ? "yes" : "no", {});
					return false;
				} else {
					property.properties.is_valid.ensure("yes", {});
					return true;
				}
			})(),
		false
	)
);

const fileNotSet = WeakCache<api_cs.Cfile__type__property, ScalarProvider<boolean>>((property) =>
	createVariable(
		toStream(property.properties.is_editable).stateSwitch({
			yes: ($) =>
				toStream(property.component_root.properties.location).stateSwitch({
					remote: () => false,
					local: () =>
						toStream($.properties.modified).stateSwitch({
							no: () => true,
							yes: () => false,
						})(),
				})(),
			no: () => false,
		})(),
		false
	)
);
const fileExtensionNotValid = WeakCache<api_cs.Cfile__type__property, Scalar<boolean>>((property) => {
	const validation_pattern = getFileExtensionValidationPattern(property);
	if (!validation_pattern) return false;
	return createVariable(
		toStream(property.properties.is_editable).stateSwitch({
			yes: ($) =>
				toStream($.properties.modified).stateSwitch({
					no: () => false,
					yes: ($) => toStream($, "extension").pipe((extension) => !validateText(extension, validation_pattern))(),
				})(),
			no: () => false,
		})(),
		false
	);
});
const fileValid = WeakCache<api_cs.Cfile__type__property, ScalarProvider<boolean>>((property) =>
	createVariable(
		createExpressionBuilder(fileNotSet(property))
			.and(fileExtensionNotValid(property))
			.pipe(([not_set, extension_not_valid]) => {
				if (not_set || extension_not_valid) {
					const invalid = property.properties.is_valid.ensure("no", {
						"not set": ["no", {}],
						"extension not valid": ["no", {}],
					});
					invalid.properties.not_set.ensure(not_set ? "yes" : "no", {});
					if (extension_not_valid) {
						invalid.properties.extension_not_valid.ensure("yes", {
							pattern: getFileExtensionValidationPattern(property)!.properties.regular_expression,
						});
					} else {
						invalid.properties.extension_not_valid.ensure("no", {});
					}
					return false;
				} else {
					property.properties.is_valid.ensure("yes", {});
					return true;
				}
			})(),
		false
	)
);

const stategroupNoStateSelected = WeakCache<api_cs.Cstate_group__type__property, ScalarProvider<boolean>>((property) =>
	createVariable(
		toStream(property.properties.is_set).stateSwitch({
			yes: () => false,
			no: () =>
				toStream(property.component_root.properties.location).stateSwitch({
					remote: () =>
						toStream(property.properties.is_modified).stateSwitch({
							no: () => false,
							yes: () => true,
						})(),
					local: () => true,
				})(),
		})(),
		false
	)
);
const stategroupInvalidRules = WeakCache<api_cs.Cstate_group__type__property, ScalarProvider<boolean>>((property) =>
	createVariable(
		toStream(property.properties.is_set).stateSwitch({
			no: () => false,
			yes: ($) =>
				createExpressionBuilder(rulesValid($.properties.state.ref.properties.rules)).pipe((valid) => !valid)(),
		})(),
		false
	)
);
const stategroupStateNodeValid = WeakCache<api_cs.Cstate_group__type__property, ScalarProvider<boolean | Indirect>>(
	(property) =>
		createVariable(
			toStream(property.properties.is_set).stateSwitch({
				no: () => true,
				yes: ($) => createExpressionBuilder(nodeValid($.properties.state.ref.properties.node))(),
			})(),
			false
		)
);
const stategroupValid = WeakCache<api_cs.Cstate_group__type__property, ScalarProvider<boolean | Indirect>>((property) =>
	createVariable(
		createExpressionBuilder(stategroupNoStateSelected(property))
			.and(stategroupInvalidRules(property))
			.and(stategroupStateNodeValid(property))
			.pipe(([[no_state_selected, invalid_rules], state_node_valid]) => {
				const direct_invalid = no_state_selected || invalid_rules || state_node_valid === false;
				const indirect_invalid = state_node_valid === Indirect;
				if (direct_invalid || indirect_invalid) {
					const invalid = property.properties.is_valid.ensure("no", {
						"no state selected": ["no", {}],
						"invalid rules": ["no", {}],
					});
					invalid.properties.no_state_selected.ensure(no_state_selected ? "yes" : "no", {});
					invalid.properties.invalid_rules.ensure(invalid_rules ? "yes" : "no", {});
					if (!direct_invalid) {
						return Indirect;
					}
					return false;
				} else {
					property.properties.is_valid.ensure("yes", {});
					return true;
				}
			})(),
		false
	)
);

export function reduceValid(accumulator: boolean | Indirect, value: boolean | Indirect) {
	return value === false || accumulator === false
		? false
		: value === Indirect || accumulator === Indirect
		? Indirect
		: true;
}
export const nodeValid: (node: api_cs.Cnode) => ScalarProvider<boolean | Indirect> = WeakCache<
	api_cs.Cnode,
	ScalarProvider<boolean | Indirect>
>((node) =>
	createVariable(
		toStream(node.properties.attributes)
			.map(([, attribute]) => {
				const attribute_type = attribute.properties.type.state;
				switch (attribute_type.name) {
					case "operation":
						return true;
					case "property": {
						const property_type = attribute_type.node.properties.type.state;
						switch (
							attribute_type.node.inferences.property_type().properties.type.state.node.output.value_source().variant
								.name
						) {
							case "derived":
								return true;
							case "base": {
								switch (property_type.name) {
									case "text":
										return textValid(property_type.node);
									case "number":
										return numberValid(property_type.node);
									case "file":
										return fileValid(property_type.node);
									case "group":
										return nodeValid(property_type.node.properties.node);
									case "state group":
										return stategroupValid(property_type.node);
									case "collection": {
										const size =
											property_type.node.inferences.collection_type().properties.ui.properties.size.state.name;
										return createExpressionBuilder(iCollection(property_type.node).isValid).pipe((collection_valid) => {
											switch (size) {
												case "small":
													return collection_valid;
												case "large":
													return toStream(node.properties.location).stateSwitch({
														local: () => collection_valid,
														remote: () => true,
													})();
											}
										})();
									}
								}
								break;
							}
						}
					}
				}
			})
			.reduce(reduceValid, true)
			.pipe((valid) => {
				node.properties.is_valid.ensure(valid === true ? "yes" : "no", {});
				return valid;
			})(),
		false
	)
);
